import { CurrentFocusContainerMemento } from "@/types/memento";

/**
 * Generate a human-readable summary for a memento
 * Phase 2.1: Auto-generated summaries
 * Only generates for MAJOR events - user can manually edit others
 * 
 * For refine/side quest commands, use the edge function for AI summaries
 */
export function generateMementoSummary(memento: CurrentFocusContainerMemento): string {
  // If custom summary exists, use it
  if (memento.custom_summary) {
    return memento.custom_summary;
  }

  // Check for refine/side quest commands - these should use AI edge function
  const commandText = memento.analysis_command_text?.toLowerCase() || '';
  const hasAICommand = commandText.includes('refine') || 
                       commandText.includes('side quest');
  
  // Only auto-generate for major events OR AI commands
  const majorEvents = ['manual_save', 'stash', 'new_focus', 'history_load'];
  const isPinned = memento.pinned;
  const isFavorited = memento.favorited;
  
  if (!majorEvents.includes(memento.trigger_type) && !isPinned && !isFavorited && !hasAICommand) {
    return ''; // Empty for routine auto-saves
  }

  const parts: string[] = [];

  // Detect significant events - handle summary-only mementos (lazy loading)
  const commentCount = memento.analysis_comments?.length ?? 0;
  const attachmentCount = (memento.analysis_attachments?.length ?? 0) + (memento.synthesis_attachments?.length ?? 0);
  const changeCount = memento.analysis_changes?.length ?? 0;
  const wordCount = memento.analysis_current_content?.split(/\s+/).filter(Boolean).length ?? 0;

  // Event-based summaries
  if (memento.trigger_type === 'stash') {
    parts.push('Stashed before switching context');
  } else if (memento.trigger_type === 'manual_save') {
    parts.push('Manually saved');
  } else if (memento.trigger_type === 'history_load') {
    parts.push('Loaded from history');
  } else if (isPinned) {
    parts.push('Pinned checkpoint');
  } else if (isFavorited) {
    parts.push('Favorited version');
  } else if (hasAICommand) {
    // For AI commands, indicate that edge function should be called
    // The actual summary will be generated asynchronously
    if (commandText.includes('refine')) {
      parts.push('AI refinement in progress');
    } else if (commandText.includes('side quest')) {
      parts.push('Side quest started');
    }
  } else {
    // Auto-save - try to be descriptive
    if (commentCount > 0) {
      parts.push(`${commentCount} comment${commentCount > 1 ? 's' : ''}`);
    }
    if (attachmentCount > 0) {
      parts.push(`${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}`);
    }
    if (changeCount > 0) {
      parts.push(`${changeCount} change${changeCount > 1 ? 's' : ''}`);
    }
  }

  // Content-based description
  if (wordCount > 0) {
    if (wordCount < 10) {
      parts.push('minimal content');
    } else if (wordCount < 50) {
      parts.push('brief notes');
    } else if (wordCount < 200) {
      parts.push('short analysis');
    } else if (wordCount < 500) {
      parts.push('detailed analysis');
    } else {
      parts.push('comprehensive analysis');
    }
  } else {
    parts.push('empty draft');
  }

  if (parts.length === 0) {
    return 'Empty state';
  }

  // Capitalize first letter
  const summary = parts.join(' · ');
  return summary.charAt(0).toUpperCase() + summary.slice(1);
}

/**
 * Check if a memento should get an AI-generated summary via edge function
 */
export function shouldGenerateAISummary(memento: CurrentFocusContainerMemento): boolean {
  const commandText = memento.analysis_command_text?.toLowerCase() || '';
  return commandText.includes('refine') || 
         commandText.includes('side quest');
}

/**
 * Event types map 1:1 to trigger types
 * Simplified: no content analysis needed
 * 
 * Trigger Type Meanings:
 * - new_focus: Brand new empty state (user started fresh)
 * - side_quest: Detour to solve something else first  
 * - stash: Manual stash (save current work, switch to something else)
 * - version: Manual version/snapshot (Ctrl+S or version button)
 * - manual_save: User explicitly clicked save button
 * - history_load: User loaded historical memento from timeline
 * - auto_save: Background auto-save every 30 seconds
 *
 *            (e.g., AI generated synthesis, summary, or performed refinement)
 */
export type MementoEventType = 
  | 'new_focus'        // Brand new empty state
  | 'side_quest'       // Detour to solve something first
  | 'stash'            // Manual stash
  | 'version'          // Manual version (Ctrl+S)
  | 'manual_save'      // User clicked save
  | 'history_load'     // Loaded from history
  | 'auto_save'        // Background auto-save

export function detectMementoEvent(
  memento: CurrentFocusContainerMemento
): MementoEventType {
  // Pin/Favorite override trigger_type for display purposes
  // User-marked items are not "noise" even if auto-saved
  if (memento.pinned) return 'version'; // Treat pinned as checkpoint
  if (memento.favorited) return 'version'; // Treat favorited as checkpoint
  
  // Direct 1:1 mapping from trigger_type to event type
  if (memento.trigger_type === 'new_focus') return 'new_focus';
  if (memento.trigger_type === 'side_quest') return 'side_quest';
  if (memento.trigger_type === 'stash') return 'stash';
  if (memento.trigger_type === 'version') return 'version';
  if (memento.trigger_type === 'manual_save') return 'manual_save';
  if (memento.trigger_type === 'history_load') return 'history_load';
  if (memento.trigger_type === 'auto_save') return 'auto_save';
  
  // Legacy trigger types (from before standardization)
  // @ts-expect-error - handling old data with invalid trigger types
  if (memento.trigger_type === 'viewed') return 'history_load';
  if (memento.trigger_type === 'switched_focus') return 'manual_save';

  // Fallback (should never happen with valid data)
  return 'auto_save';
}

/**
 * Get a color for the event type (for timeline visualization)
 */
export function getEventColor(eventType: MementoEventType): string {
  switch (eventType) {
    case 'new_focus': return 'hsl(var(--chart-1))'; // Blue
    case 'side_quest': return 'hsl(var(--chart-3))'; // Purple  
    case 'stash': return 'hsl(var(--chart-3))'; // Purple
    case 'version': return 'hsl(var(--chart-2))'; // Green
    case 'manual_save': return 'hsl(var(--chart-2))'; // Green
    case 'history_load': return 'hsl(var(--chart-4))'; // Yellow
    case 'auto_save': return 'hsl(var(--muted-foreground))'; // Gray
    default: return 'hsl(var(--muted-foreground))'; // Gray
  }
}

/**
 * Get a human-readable label for the event type
 */
export function getEventLabel(eventType: MementoEventType): string {
  switch (eventType) {
    case 'new_focus': return 'New Focus';
    case 'side_quest': return 'Side Quest';
    case 'stash': return 'Stashed';
    case 'version': return 'Version';
    case 'manual_save': return 'Saved';
    case 'history_load': return 'Loaded';
    case 'auto_save': return 'Auto-save';
  }
}
